import { IPublicClientApplication } from '@azure/msal-browser';
import { dvList, dvCreate, dvUpdate, dvDelete } from '../api/dataverseClient';

export interface ApprovalChain {
  dagrun_pm_approvalchainid: string;
  dagrun_pm_name: string;
  dagrun_pm_projectid?: string | null;
  dagrun_pm_entitytype?: number | null;
  dagrun_pm_stages?: number | null;
  dagrun_pm_isactive?: boolean | null;
  // Per-entity ad-hoc chains (Task/Milestone/RFP) bind the chain directly to
  // one entity instead of acting as a reusable project template.
  dagrun_pm_entityid?: string | null;
  dagrun_pm_entitytitle?: string | null;
  dagrun_pm_requirecompletion?: boolean | null;
}

export interface ApprovalStage {
  dagrun_pm_approvalstageid: string;
  dagrun_pm_name: string;
  dagrun_pm_approvalchainid?: string | null;
  dagrun_pm_stageorder?: number | null;
  dagrun_pm_approverid?: string | null;
  dagrun_pm_approvername?: string | null;
  dagrun_pm_status?: number | null;
  dagrun_pm_decidedon?: string | null;
  dagrun_pm_comments?: string | null;
  dagrun_pm_entityid?: string | null;  // null = template stage on a chain; set = stage instance against a specific entity (submittal/rfi/etc.)
  dagrun_pm_deadline?: string | null;
  dagrun_pm_instructions?: string | null;
  createdon?: string;
}

export const ApprovalEntityType = {
  Submittal:  1,
  RFI:        2,
  PunchItem:  3,
  Custom:     4,
  Task:       5,
  Milestone:  6,
  RFP:        7,
} as const;

export const ApprovalStageStatus = {
  Pending:  1,
  Approved: 2,
  Rejected: 3,
  Skipped:  4,
} as const;

const CHAIN_FIELDS = [
  'dagrun_pm_approvalchainid', 'dagrun_pm_name', 'dagrun_pm_projectid',
  'dagrun_pm_entitytype', 'dagrun_pm_stages', 'dagrun_pm_isactive',
  'dagrun_pm_entityid', 'dagrun_pm_entitytitle', 'dagrun_pm_requirecompletion',
];
const STAGE_FIELDS = [
  'dagrun_pm_approvalstageid', 'dagrun_pm_name', 'dagrun_pm_approvalchainid',
  'dagrun_pm_stageorder', 'dagrun_pm_approverid', 'dagrun_pm_approvername',
  'dagrun_pm_status', 'dagrun_pm_decidedon', 'dagrun_pm_comments',
  'dagrun_pm_entityid', 'dagrun_pm_deadline', 'dagrun_pm_instructions', 'createdon',
];

// ── Chains ─────────────────────────────────────────────────────────

export async function getProjectApprovalChains(pca: IPublicClientApplication, projectId: string): Promise<ApprovalChain[]> {
  return dvList<ApprovalChain>(pca, 'dagrun_pm_approvalchains', {
    select: CHAIN_FIELDS,
    filter: `dagrun_pm_projectid eq '${projectId}'`,
    orderby: 'dagrun_pm_name asc',
    top: 200,
  });
}

export async function findProjectChainForEntityType(
  pca: IPublicClientApplication,
  projectId: string,
  entityType: number,
): Promise<ApprovalChain | null> {
  const chains = await dvList<ApprovalChain>(pca, 'dagrun_pm_approvalchains', {
    select: CHAIN_FIELDS,
    filter: `dagrun_pm_projectid eq '${projectId}' and dagrun_pm_entitytype eq ${entityType} and dagrun_pm_isactive eq true`,
    top: 1,
  });
  return chains[0] ?? null;
}

export interface ChainStageTemplate {
  label: string;
  approverid: string;
  approvername: string;
}

export async function createChainWithStages(
  pca: IPublicClientApplication,
  projectId: string,
  chain: { name: string; entitytype: number; stages: ChainStageTemplate[]; isactive?: boolean },
): Promise<string> {
  const chainId = await dvCreate(pca, 'dagrun_pm_approvalchains', {
    dagrun_pm_name: chain.name,
    dagrun_pm_projectid: projectId,
    dagrun_pm_entitytype: chain.entitytype,
    dagrun_pm_stages: chain.stages.length,
    dagrun_pm_isactive: chain.isactive ?? true,
  });
  // Create template stage records (entityid left null — instances get the entityid).
  for (let i = 0; i < chain.stages.length; i++) {
    const s = chain.stages[i];
    await dvCreate(pca, 'dagrun_pm_approvalstages', {
      dagrun_pm_name: s.label || `Stage ${i + 1}`,
      dagrun_pm_approvalchainid: chainId,
      dagrun_pm_stageorder: i + 1,
      dagrun_pm_approverid: s.approverid,
      dagrun_pm_approvername: s.approvername,
      dagrun_pm_status: ApprovalStageStatus.Pending,
    });
  }
  return chainId;
}

export async function updateChain(pca: IPublicClientApplication, id: string, patch: Record<string, unknown>): Promise<void> {
  return dvUpdate(pca, 'dagrun_pm_approvalchains', id, patch);
}

// Delete a chain and all its template stages + all instance stages (stages
// with entityid set that reference this chain). Dataverse doesn't cascade
// across relationship tables we haven't explicitly configured, so this is
// a manual cascade to avoid orphan rows.
export async function deleteChain(pca: IPublicClientApplication, chainId: string): Promise<void> {
  const stages = await dvList<ApprovalStage>(pca, 'dagrun_pm_approvalstages', {
    select: ['dagrun_pm_approvalstageid'],
    filter: `dagrun_pm_approvalchainid eq '${chainId}'`,
    top: 500,
  });
  for (const s of stages) {
    try { await dvDelete(pca, 'dagrun_pm_approvalstages', s.dagrun_pm_approvalstageid); }
    catch { /* best effort, continue */ }
  }
  return dvDelete(pca, 'dagrun_pm_approvalchains', chainId);
}

// ── Stages ─────────────────────────────────────────────────────────

// Template stages: chain's stages with entityid null.
export async function getChainTemplateStages(pca: IPublicClientApplication, chainId: string): Promise<ApprovalStage[]> {
  return dvList<ApprovalStage>(pca, 'dagrun_pm_approvalstages', {
    select: STAGE_FIELDS,
    filter: `dagrun_pm_approvalchainid eq '${chainId}' and dagrun_pm_entityid eq null`,
    orderby: 'dagrun_pm_stageorder asc',
    top: 50,
  });
}

// Instance stages for a given entity (e.g. a specific submittal).
export async function getEntityStages(pca: IPublicClientApplication, entityId: string): Promise<ApprovalStage[]> {
  return dvList<ApprovalStage>(pca, 'dagrun_pm_approvalstages', {
    select: STAGE_FIELDS,
    filter: `dagrun_pm_entityid eq '${entityId}'`,
    orderby: 'dagrun_pm_stageorder asc',
    top: 50,
  });
}

// Create instance stages for a specific entity from the chain's templates.
// Returns the count of stages created. Safe to call only when the entity
// has no existing instance stages — caller should check first.
export async function instantiateStagesForEntity(
  pca: IPublicClientApplication,
  chainId: string,
  entityId: string,
  entityLabel: string,
): Promise<number> {
  const templates = await getChainTemplateStages(pca, chainId);
  for (const t of templates) {
    // TODO: Teams Adaptive Card notification
    // When a stage is created and assigned to an approver, send an Adaptive
    // Card via Power Automate flow (to be built in a future session).
    // Payload shape:
    // {
    //   approverAadId: string,           // dagrun_pm_approverid
    //   submittalNumber: string,         // entityLabel — e.g. the submittal number
    //   projectName: string,             // not captured here — lookup from chain.projectid
    //   stageName: string,               // dagrun_pm_name on the stage
    //   stageOrder: number,
    //   approveDeepLink: string,         // deep link to pm.dagrun.io/#/projects/{id}/construction
    //   rejectDeepLink: string
    // }
    await dvCreate(pca, 'dagrun_pm_approvalstages', {
      dagrun_pm_name: `${t.dagrun_pm_name} — ${entityLabel}`,
      dagrun_pm_approvalchainid: chainId,
      dagrun_pm_stageorder: t.dagrun_pm_stageorder ?? 0,
      dagrun_pm_approverid: t.dagrun_pm_approverid ?? null,
      dagrun_pm_approvername: t.dagrun_pm_approvername ?? null,
      dagrun_pm_status: ApprovalStageStatus.Pending,
      dagrun_pm_entityid: entityId,
    });
  }
  return templates.length;
}

export async function approveStage(pca: IPublicClientApplication, stageId: string, comments?: string | null): Promise<void> {
  const patch: Record<string, unknown> = {
    dagrun_pm_status: ApprovalStageStatus.Approved,
    dagrun_pm_decidedon: new Date().toISOString(),
  };
  if (comments) patch.dagrun_pm_comments = comments;
  return dvUpdate(pca, 'dagrun_pm_approvalstages', stageId, patch);
}

export async function rejectStage(pca: IPublicClientApplication, stageId: string, comments: string): Promise<void> {
  return dvUpdate(pca, 'dagrun_pm_approvalstages', stageId, {
    dagrun_pm_status: ApprovalStageStatus.Rejected,
    dagrun_pm_decidedon: new Date().toISOString(),
    dagrun_pm_comments: comments,
  });
}

export async function updateStage(pca: IPublicClientApplication, stageId: string, patch: Record<string, unknown>): Promise<void> {
  return dvUpdate(pca, 'dagrun_pm_approvalstages', stageId, patch);
}

export async function deleteStage(pca: IPublicClientApplication, stageId: string): Promise<void> {
  return dvDelete(pca, 'dagrun_pm_approvalstages', stageId);
}

// ── Per-entity ad-hoc chains (Task / Milestone / RFP) ──────────────────
// Unlike the project-template chains above, these bind one chain to one
// entity via dagrun_pm_entityid on both the chain and its stages, and the
// stages are created as instances directly (deadline + instructions).

export const ENTITY_TYPE_LABEL: Record<number, string> = {
  [ApprovalEntityType.Submittal]: 'Submittal',
  [ApprovalEntityType.RFI]: 'RFI',
  [ApprovalEntityType.PunchItem]: 'Punch Item',
  [ApprovalEntityType.Custom]: 'Custom',
  [ApprovalEntityType.Task]: 'Task',
  [ApprovalEntityType.Milestone]: 'Milestone',
  [ApprovalEntityType.RFP]: 'RFP',
};

export interface EntityChainStageInput {
  label: string;
  approverid: string;
  approvername: string;
  deadline?: string | null;
  instructions?: string | null;
}

export async function createEntityApprovalChain(
  pca: IPublicClientApplication,
  params: {
    projectId?: string | null;
    entityType: number;
    entityId: string;
    entityTitle: string;
    requireCompletion: boolean;
    stages: EntityChainStageInput[];
  },
): Promise<string> {
  const chainBody: Record<string, unknown> = {
    dagrun_pm_name: `${params.entityTitle} — approval`.slice(0, 100),
    dagrun_pm_entitytype: params.entityType,
    dagrun_pm_entityid: params.entityId,
    dagrun_pm_entitytitle: params.entityTitle.slice(0, 400),
    dagrun_pm_requirecompletion: params.requireCompletion,
    dagrun_pm_stages: params.stages.length,
    dagrun_pm_isactive: true,
  };
  if (params.projectId) chainBody.dagrun_pm_projectid = params.projectId;
  const chainId = await dvCreate(pca, 'dagrun_pm_approvalchains', chainBody);

  // Stage 1 is active on creation — all stages start Pending; the first
  // Pending stage in order is "active" by the state machine in the UI.
  for (let i = 0; i < params.stages.length; i++) {
    const s = params.stages[i];
    await dvCreate(pca, 'dagrun_pm_approvalstages', {
      dagrun_pm_name: s.label || `Stage ${i + 1}`,
      dagrun_pm_approvalchainid: chainId,
      dagrun_pm_stageorder: i + 1,
      dagrun_pm_approverid: s.approverid,
      dagrun_pm_approvername: s.approvername,
      dagrun_pm_status: ApprovalStageStatus.Pending,
      dagrun_pm_entityid: params.entityId,
      dagrun_pm_deadline: s.deadline ?? null,
      dagrun_pm_instructions: s.instructions ?? null,
    });
  }
  return chainId;
}

/** The ad-hoc chain bound to a specific entity, if any. */
export async function getEntityChain(pca: IPublicClientApplication, entityId: string): Promise<ApprovalChain | null> {
  const rows = await dvList<ApprovalChain>(pca, 'dagrun_pm_approvalchains', {
    select: CHAIN_FIELDS,
    filter: `dagrun_pm_entityid eq '${entityId}'`,
    top: 1,
  });
  return rows[0] ?? null;
}

/** Reset every stage of an entity's chain back to Pending (chain restart). */
export async function resetEntityStages(pca: IPublicClientApplication, entityId: string): Promise<void> {
  const stages = await getEntityStages(pca, entityId);
  for (const s of stages) {
    await dvUpdate(pca, 'dagrun_pm_approvalstages', s.dagrun_pm_approvalstageid, {
      dagrun_pm_status: ApprovalStageStatus.Pending,
      dagrun_pm_decidedon: null,
      dagrun_pm_comments: null,
    });
  }
}

/** Delete an entity's chain and all its instance stages. */
export async function deleteEntityChain(pca: IPublicClientApplication, entityId: string): Promise<void> {
  const chain = await getEntityChain(pca, entityId);
  const stages = await getEntityStages(pca, entityId);
  for (const s of stages) {
    try { await dvDelete(pca, 'dagrun_pm_approvalstages', s.dagrun_pm_approvalstageid); } catch { /* continue */ }
  }
  if (chain) await dvDelete(pca, 'dagrun_pm_approvalchains', chain.dagrun_pm_approvalchainid);
}
